import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { IImperansReferencePick } from "../../Interfaces/IImperansReference";
import {
  COUNTRY_NEEDED_MESSAGE,
  IMPERANS_UNAVAILABLE_MESSAGE,
  IReferencePickerOption,
  isPickStale,
  pickFromOption,
  STALE_PICK_MESSAGE,
} from "./ImperansReference.rules";
import { IImperansReferenceList } from "./useImperansReferenceList";

type Props = {
  label: string;
  list: IImperansReferenceList;
  /** False until the client's country is known — nothing can be listed before then. */
  hasCountry: boolean;
  value: IImperansReferencePick | null;
  onChange: (pick: IImperansReferencePick | null) => void;
  required?: boolean;
  disabled?: boolean;
  /** Set by the owning form once an advance was refused; the message beneath says why. */
  error?: boolean;
  helperText?: React.ReactNode;
  size?: "small" | "medium";
  fullWidth?: boolean;
};

export const RETRY_LABEL = "Retry";
const STALE_SUFFIX = "no longer offered";

/** Searching matches the name and the line beneath it — a clinic can be found by its franchise. */
const filterOptions = createFilterOptions<IReferencePickerOption>({
  stringify: (option) => `${option.label} ${option.secondary ?? ""}`,
});

/**
 * A searchable pick from one of the paired Imperans's reference lists (Epic 6303, Feature CRM3).
 *
 * The list is whatever Imperans offers right now. A stored pick that is no longer in it stays
 * visible — marked, with the reason — so the reader sees what was chosen and is told to choose
 * again (story 6374). When Imperans cannot be reached the field says so and offers Retry rather
 * than a silently empty dropdown.
 */
const ImperansReferencePicker: React.FC<Props> = ({
  label,
  list,
  hasCountry,
  value,
  onChange,
  required,
  disabled,
  error,
  helperText,
  size = "small",
  fullWidth = true,
}) => {
  const offered = list.loaded ? list.options : null;
  const stale = isPickStale(value?.id, offered);

  // The stored pick is always an option, so the field shows it even when the list has moved on.
  const selected: IReferencePickerOption | null =
    value?.id === null || value?.id === undefined
      ? null
      : list.options.find((option) => option.id === value.id) ?? {
          id: value.id,
          label: value.name || `#${value.id}`,
          secondary: stale ? STALE_SUFFIX : null,
        };
  const options =
    selected && !list.options.some((option) => option.id === selected.id)
      ? [selected, ...list.options]
      : list.options;

  const message = error
    ? helperText
    : stale
      ? STALE_PICK_MESSAGE
      : list.unavailable
        ? IMPERANS_UNAVAILABLE_MESSAGE
        : !hasCountry
          ? COUNTRY_NEEDED_MESSAGE
          : helperText;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", width: fullWidth ? "100%" : undefined }}>
      <Autocomplete
        fullWidth={fullWidth}
        size={size}
        options={options}
        value={selected}
        loading={list.loading}
        disabled={disabled || !hasCountry}
        filterOptions={filterOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        onChange={(_, option) => onChange(pickFromOption(option))}
        noOptionsText={list.unavailable ? IMPERANS_UNAVAILABLE_MESSAGE : "No matches"}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.label}</Typography>
              {option.secondary && (
                <Typography variant="caption" color="text.secondary">
                  {option.secondary}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={Boolean(error) || stale || list.unavailable}
            helperText={message || " "}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {list.loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {list.unavailable && !disabled && (
        <Button size="small" sx={{ mt: 0.5, whiteSpace: "nowrap" }} onClick={list.retry}>
          {RETRY_LABEL}
        </Button>
      )}
    </Box>
  );
};

export default ImperansReferencePicker;
